import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router";

import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { DataTable } from "@/components/table/DataTable";
import type { DataTableColumn } from "@/components/table/columns";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, PropertyListItemResource, ReferenceListItemResource } from "@/lib/api-types";
import { useIsOwner } from "@/lib/current-actor";
import { formatDateCell, formatMoneyCell } from "@/lib/format";
import {
  buildListSearch,
  parsePageParam,
  parseSortParam,
  pageSizeFor,
  PAGE_PARAM,
  SORT_PARAM,
} from "@/lib/list-query";
import { useIsPhone } from "@/lib/use-media-query";

import { AddPropertyWizard } from "./AddPropertyWizard";
import { PropertiesReportDialog } from "./PropertiesReportDialog";

/**
 * `/properties` (Story 5.3, judgement call 3; extended by Story 15.13,
 * 15.24). The shared `DataTable` (Epic 2), configured with only the columns
 * this story itself can query — nickname, full address, municipality,
 * property type. No `occupancy`/`monthly_rent`/`next_lease_expiration`/
 * `open_alerts` column exists here at all (VC-5.3-05); a later story (5.4
 * for units, Epic 6 for tenants/leases, whichever ships alert-per-entity
 * querying) appends its own column to this same array — this story's own
 * code needs no change then.
 *
 * **Story 15.13** is that "later story" for lease dates specifically — a
 * deliberate, second exception to the narrow-column-set convention above
 * (the first being Story 15.12's Lease start/end columns on the Tenants
 * list), Owner-confirmed rather than re-litigated here. Judgement call 1
 * confirmed directly: this list had no Lease-end column at all before this
 * story (`git log` / this file's own pre-15.13 history), so both "Lease
 * start" and "Lease end" are new columns, not an extension of an existing
 * one. Both are `sortable: false`, mirroring `TenantsList.tsx`'s own
 * identical reasoning for its analogous pair: these are bulk-resolved,
 * computed values (`PropertyListItemResource.soonest_lease_start_date`/
 * `soonest_lease_end_date`, `GET /properties`'s own one-additional-query
 * resolution, `app/repo/leases.py::bulk_soonest_upcoming_lease_dates_for_
 * properties`), not a real, sortable column this list's own query grammar
 * knows how to sort by — building that sort machinery is out of this
 * story's scope, the identical judgement Story 15.12 already made.
 *
 * **Story 15.24** (Owner-requested) removes "Year built" and replaces it in
 * the same position with "Tenant" — the tenant(s) of the exact same
 * soonest-upcoming-to-end lease already backing "Lease start"/"Lease end"
 * immediately to its right (`PropertyListItemResource.tenants`, resolved
 * server-side from that identical lease's own id, never a second,
 * independently-chosen one — `GET /properties`'s own doc comment,
 * `app/api/properties.py`). `year_built` itself is untouched anywhere else
 * it appears (the record page's own detail fields, the create wizard) —
 * only this list's own column set changes. Rendering mirrors Story 15.21's
 * "Tenants" column on the Leases list exactly: every tenant its own
 * separate `<Link>` to `/tenants-leases/{tenant_id}`, comma-separated, no
 * truncation; "Vacant" is this list's own honest label for the same
 * "no qualifying lease at all" condition that already leaves "Lease start"/
 * "Lease end" blank for that row (never a dash, which could read as "not
 * loaded yet," and never a fabricated name). A row that *does* have a
 * resolved lease but zero tenants attached to it (a real, legal state,
 * VC-6.5-11 — caught live against real seeded data during this story's own
 * walkthrough) renders "No tenants" instead, mirroring the Leases list's
 * own identical label for the identical condition (Story 15.21) — "Vacant"
 * would falsely claim no lease exists at all. `sortable: false` for the
 * identical reason "Tenants" is on the Leases list — a lease's co-tenants
 * are an unordered, one-to-many set with no "primary tenant" concept, so
 * "sort by tenant name" has no well-defined single key once a row has more
 * than one.
 *
 * "Archived" here is plain AD-8 soft-delete, not Story 5.6's richer concept
 * (Boundaries & Constraints) — a plain checkbox toggle, managed as local
 * component state rather than through `DataTable`'s own generic per-column
 * filter mechanism (which assumes an equality filter against a real
 * sortable/filterable column, not a soft-delete visibility toggle), mirroring
 * `ReferenceLists.tsx`'s own precedent for state this story's list doesn't
 * fit into that generic shape.
 */
export function PropertiesList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  //: `POST /properties`/`DELETE /properties/{id}` are `role=Role.OWNER`
  //: (Boundaries & Constraints) — the create affordance is withheld from
  //: anyone but a confirmed Owner, not merely "not Read-only" (a Member is
  //: not Read-only and would otherwise see this button, then 403 the
  //: moment they submitted — the same Tier A fix `MunicipalityRecord.tsx`/
  //: `PropertyRecord.tsx` both needed for the identical reason).
  const isOwner = useIsOwner();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  useEffect(() => {
    document.title = "Properties — PropDesk";
  }, []);

  const sort = parseSortParam(searchParams.get(SORT_PARAM));
  const page = parsePageParam(searchParams.get(PAGE_PARAM));
  const pageSize = pageSizeFor(isPhone);
  const search = buildListSearch({
    sort,
    filters: includeArchived ? { include_archived: "true" } : {},
    page,
    pageSize,
  });
  const listQueryKey = ["properties", "list", search] as const;

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => apiFetch<Page<PropertyListItemResource>>(`/properties?${search}`),
    placeholderData: keepPreviousData,
  });

  //: Gap-fill (2026-08-27, Owner-caught): every other reference-list-backed
  //: column in this app (Equipment.tsx's/DocumentsList.tsx's own `category`,
  //: VendorsList.tsx's own `trade`) resolves the stored machine `value` to
  //: its real, human-readable `label` before rendering — this was the one
  //: column that never got that treatment, silently showing the raw stored
  //: value instead of its real label. Mirrors those three files' identical
  //: `labelByValue` shape exactly.
  const propertyTypesQuery = useQuery({
    queryKey: ["reference-list-items", "property_type"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=property_type"),
  });
  const propertyTypeLabelByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of propertyTypesQuery.data?.data ?? []) {
      map[item.value] = item.label;
    }
    return map;
  }, [propertyTypesQuery.data]);

  const columns = useMemo<DataTableColumn<PropertyListItemResource>[]>(
    () => [
      {
        // Owner-requested (2026-08-28): real sort support landed on
        // `GET /properties` (`properties_repo.SORTABLE_FIELDS`) for
        // `nickname`/`full_address`/`property_type` — the three columns
        // below that are genuine `Property` columns this route's own query
        // already selects. `sortable` defaults to `true` (`columns.ts`),
        // so these three needed no change beyond the backend accepting the
        // request; `municipality_name`/`tenants`/the two lease-date columns
        // stay `sortable: false` — see each one's own comment for why.
        key: "nickname",
        render: (row) => (
          <span className="inline-flex items-center gap-2">
            {row.nickname}
            {row.is_archived ? (
              <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-meta text-ink-secondary">
                Archived
              </span>
            ) : null}
          </span>
        ),
      },
      { key: "full_address" },
      {
        // Real column on `Property`, but resolved here via a Python-side
        // id-to-name cache rather than a join `properties_repo`'s own query
        // already performs (`app/api/properties.py`'s own `municipality_
        // name` resolution) — sorting it would need that join built first,
        // out of this fix's own scope (`properties_repo.SORTABLE_FIELDS`'s
        // own docstring has the full reasoning).
        key: "municipality_name",
        sortable: false,
      },
      {
        key: "property_type",
        render: (row) => propertyTypeLabelByValue[row.property_type] ?? row.property_type,
      },
      {
        // Story 15.24 — replaces "Year built" in this same position. Field
        // name/label ("Tenants") reused verbatim from `LeaseListItemResource.
        // tenants` (`lib/field-labels.ts`'s own "one field name, one label,
        // everywhere it appears" convention) — no per-resource label
        // override introduced just to read "Tenant" singular here instead.
        // `sortable: false`: bulk-resolved per page from a different table
        // entirely, with no single well-defined value once a property has
        // more than one upcoming lease/tenant — genuinely not a column to
        // sort by, not merely an unbuilt one (`properties_repo.SORTABLE_
        // FIELDS`'s own docstring has the full reasoning).
        key: "tenants",
        sortable: false,
        render: (row) => {
          if (row.tenants && row.tenants.length > 0) {
            return (
              <>
                {row.tenants.map((tenant, index) => (
                  <span key={tenant.tenant_id}>
                    {index > 0 ? ", " : null}
                    <Link
                      to={`/tenants-leases/${tenant.tenant_id}`}
                      className="text-ink underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {tenant.full_name}
                    </Link>
                  </span>
                ))}
              </>
            );
          }
          // "Vacant" is honest only for the AC's own "no qualifying lease at
          // all" condition — the same one that already leaves "Lease start"/
          // "Lease end" blank for this row. A row that *does* have a
          // resolved lease (real, seeded example caught in this story's own
          // live walkthrough: a lease created before any tenant was ever
          // attached, VC-6.5-11's own legal state) but zero tenants on it is
          // a different, narrower condition — calling that "Vacant" too
          // would falsely imply no lease exists at all. Mirrors the Leases
          // list's own "No tenants" label for the identical zero-tenant-on-
          // a-real-lease case (Story 15.21).
          return row.soonest_lease_start_date || row.soonest_lease_end_date ? "No tenants" : "Vacant";
        },
      },
      {
        // Owner-requested (2026-08-28) — the sum across every in-force
        // lease on the property (`app.repo.leases.bulk_in_force_rent_for_
        // properties`'s own docstring), never tied to the single lease
        // `tenants`/the two date columns resolve — a property can show a
        // real rent figure here with "No qualifying lease" on those, e.g.
        // every in-force lease is month-to-month. `sortable: false` for the
        // identical bulk-resolved-per-page reason those columns already
        // document.
        key: "in_force_monthly_rent_cents",
        tabularFigures: true,
        align: "right",
        sortable: false,
        render: (row) => formatMoneyCell(row.in_force_monthly_rent_cents ?? 0),
      },
      {
        // `sortable: false` on both date columns below: the same
        // bulk-resolved-per-page, no-single-well-defined-value shape
        // `tenants` above already documents — sorting "soonest upcoming
        // lease" across a page whose properties each surface a different
        // resolved lease has no honest single column behind it.
        key: "soonest_lease_start_date",
        tabularFigures: true,
        sortable: false,
        render: (row) => (row.soonest_lease_start_date ? formatDateCell(row.soonest_lease_start_date) : "—"),
      },
      {
        key: "soonest_lease_end_date",
        tabularFigures: true,
        sortable: false,
        render: (row) => (row.soonest_lease_end_date ? formatDateCell(row.soonest_lease_end_date) : "—"),
      },
    ],
    [propertyTypeLabelByValue],
  );

  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <div className="mb-2 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Properties</h1>
          <p className="mt-2 text-body text-ink-secondary">Every rental property in the portfolio.</p>
        </div>
        {isOwner ? (
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <Button type="button" variant="ghost" onClick={() => setExportOpen(true)} className="w-full md:w-auto">
              Export CSV
            </Button>
            <Button type="button" onClick={() => setCreateOpen(true)} className="w-full md:w-auto">
              Add a property
            </Button>
          </div>
        ) : null}
      </div>

      <div className="mb-4">
        <Checkbox checked={includeArchived} onChange={(event) => setIncludeArchived(event.target.checked)}>
          Include archived properties
        </Checkbox>
      </div>

      <DataTable
        caption="Properties"
        itemNamePlural="properties"
        tint="indigo"
        columns={columns}
        page={listQuery.data}
        isPending={listQuery.isPending}
        isFetching={listQuery.isFetching}
        isError={listQuery.isError}
        {...(errorMessage ? { errorMessage } : {})}
        getRowHref={(row) => `/properties/${row.id}`}
        emptyState={
          isOwner
            ? {
                heading: "No properties yet.",
                body: "Add the first property to start tracking it.",
                action: { label: "Add a property", onClick: () => setCreateOpen(true) },
              }
            : {
                heading: "No properties yet.",
                body: "Properties will appear here once an Owner adds one.",
              }
        }
      />

      {isOwner ? (
        <AddPropertyWizard
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => navigate(`/properties/${id}`)}
        />
      ) : null}
      {isOwner ? <PropertiesReportDialog open={exportOpen} onOpenChange={setExportOpen} /> : null}
    </div>
  );
}
